import { createFileRoute } from "@tanstack/react-router";
import { useState, useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import {
  History,
  Filter,
  ExternalLink,
  Eye,
  Loader2,
  CheckCircle2,
  AlertTriangle,
  HelpCircle,
  CalendarDays,
  Download,
  Share2,
} from "lucide-react";

import { Header } from "@/components/layout/Header";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { InfoTooltip } from "@/components/ui/tooltip";
import { listAllSimulations, exportSimulationsExcel } from "@/lib/api/simulations";
import { listSimulators } from "@/lib/api/simulators";
import { listAllOutputFieldGroups } from "@/lib/api/output-fields";
import { UserFilter } from "@/components/dashboard/UserFilter";
import { formatNumber } from "@/lib/utils";
import { isOutputFieldVisible } from "@/lib/visibility";
import { ViewerGuard } from "@/lib/auth/AdminGuard";
import type {
  SimulationAdmin,
  OutputField,
  OutputFieldGroupWithFields,
} from "@/types";

type SharedFilterValue = "all" | "shared" | "not_shared";

interface SimulationsSearchParams {
  policyFilter?: string;
  shared?: SharedFilterValue;
}

export const Route = createFileRoute("/_dashboard/admin/simulations")({
  validateSearch: (search: Record<string, unknown>): SimulationsSearchParams => ({
    policyFilter: typeof search.policyFilter === "string" ? search.policyFilter : undefined,
    shared:
      search.shared === "shared" || search.shared === "not_shared" || search.shared === "all"
        ? (search.shared as SharedFilterValue)
        : undefined,
  }),
  component: AdminSimulationsPage,
});

// ============== Helper: format date in Paris timezone ==============

function formatDateParis(dateStr: string): string {
  let d: Date;
  if (!dateStr.endsWith("Z") && !dateStr.includes("+")) {
    d = new Date(dateStr + "Z");
  } else {
    d = new Date(dateStr);
  }
  return new Intl.DateTimeFormat("fr-FR", {
    timeZone: "Europe/Paris",
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  }).format(d);
}

// ============== Helper: format output field value ==============

function formatOutputValue(
  field: OutputField,
  value: unknown
): string {
  if (value === null || value === undefined) return "-";

  if (typeof value === "number") {
    const decimals = field.decimal_places ?? 2;
    let formatted = formatNumber(value, decimals);
    if (field.prefix) formatted = field.prefix + formatted;
    if (field.suffix) formatted = formatted + field.suffix;
    return formatted;
  }

  return String(value);
}

// ============== Helper: extract margin value from simulation inputs ==============

function getMargeFromInputs(inputs: Record<string, unknown> | undefined | null): unknown {
  if (!inputs) return undefined;
  const base = inputs.marge ?? inputs.marge_en;
  if (base != null) return base;
  // COREM Sur Mesure : la marge (ext_marge) ne s'applique qu'aux prestations
  // externalisees ; pas de controle de marge en moyens propres.
  if (inputs.type_de_prestation === "Prestation externalisée") {
    return inputs.ext_marge;
  }
  return undefined;
}

// ============== Sub-component: TarifTooltipContent ==============
// Reproduces the exact same visibility logic as the simulator page:
// - Groups output fields by their group (with parent > child naming)
// - Filters: visibility_mode "always" shown, "never" hidden,
//   "conditional" evaluated against the simulation inputs
// - Sorts fields by order within each group

function TarifTooltipContent({
  simulation,
}: {
  simulation: SimulationAdmin;
}) {
  const { data: outputFieldGroups, isLoading } = useQuery({
    queryKey: ["output-field-groups", simulation.version_id],
    queryFn: () => listAllOutputFieldGroups(simulation.version_id),
    staleTime: 5 * 60 * 1000,
  });

  // Build grouped + filtered outputs, same logic as simulate.tsx
  const visibleGroups = useMemo(() => {
    if (!outputFieldGroups) return [];

    const inputs = simulation.inputs as Record<string, unknown>;
    const groups: { name: string; fields: OutputField[] }[] = [];

    const processGroup = (
      group: OutputFieldGroupWithFields,
      parentName?: string
    ) => {
      const hideParentName =
        group.layout_options?.hide_parent_name === true;
      const groupName =
        parentName && !hideParentName
          ? `${parentName} > ${group.name}`
          : group.name;

      const outputs = simulation.outputs || {};
      const visibleFields = group.fields
        // Merge outputs so conditions can reference an output slug (inputs win)
        .filter((f) => isOutputFieldVisible(f, { ...outputs, ...inputs }))
        .filter((f) => {
          const v = outputs[f.slug];
          return v !== 0 && v !== "0" && v !== null && v !== undefined;
        })
        .sort((a, b) => a.order - b.order);

      if (visibleFields.length > 0) {
        groups.push({ name: groupName, fields: visibleFields });
      }

      group.children.forEach((child) =>
        processGroup(child, groupName)
      );
    };

    outputFieldGroups.forEach((g) => processGroup(g));
    return groups;
  }, [outputFieldGroups, simulation.inputs]);

  if (simulation.status !== "completed") {
    return (
      <p className="text-xs text-muted-foreground italic">
        Cotation non terminée
      </p>
    );
  }

  if (isLoading) {
    return (
      <div className="flex items-center gap-2 py-1">
        <Loader2 className="h-3 w-3 animate-spin" />
        <span className="text-xs">Chargement...</span>
      </div>
    );
  }

  if (visibleGroups.length === 0) {
    return (
      <p className="text-xs text-muted-foreground italic">
        Aucun résultat
      </p>
    );
  }

  return (
    <div className="space-y-2 max-h-80 overflow-y-auto">
      {visibleGroups.map((group) => (
        <div key={group.name}>
          <p className="text-[10px] font-medium text-muted-foreground uppercase tracking-wide mb-1">
            {group.name}
          </p>
          <div className="space-y-0.5">
            {group.fields.map((field) => {
              const value = simulation.outputs?.[field.slug];
              return (
                <div
                  key={field.slug}
                  className="flex items-center justify-between gap-4 text-xs"
                >
                  <span className="text-muted-foreground truncate max-w-[200px]">
                    {field.label}
                  </span>
                  <span className="font-mono font-medium whitespace-nowrap">
                    {formatOutputValue(field, value)}
                  </span>
                </div>
              );
            })}
          </div>
        </div>
      ))}
    </div>
  );
}

// ============== Main Page Component ==============

function AdminSimulationsPage() {
  const { policyFilter: initialPolicyFilter, shared: initialShared } = Route.useSearch();

  const [page, setPage] = useState(1);
  const pageSize = 20;
  const [isExporting, setIsExporting] = useState(false);

  // Filters
  const [simulatorFilter, setSimulatorFilter] = useState<string>("all");
  const [userFilter, setUserFilter] = useState<string>("all");
  const [policyFilter, setPolicyFilter] = useState<string>(initialPolicyFilter ?? "all");
  const [sharedFilter, setSharedFilter] = useState<SharedFilterValue>(initialShared ?? "all");

  const [dateFrom, setDateFrom] = useState<string>("");
  const [dateTo, setDateTo] = useState<string>("");

  const sharedOnlyParam =
    sharedFilter === "shared" ? true : sharedFilter === "not_shared" ? false : undefined;

  // Fetch simulators for filter
  const { data: simulatorsData } = useQuery({
    queryKey: ["simulators-list"],
    queryFn: () =>
      listSimulators({ page_size: 100, include_unpublished: true }),
  });

  // Fetch all simulations with filters
  const { data, isLoading } = useQuery({
    queryKey: [
      "admin-simulations",
      page,
      pageSize,
      simulatorFilter,
      userFilter,
      policyFilter,
      sharedFilter,
      dateFrom,
      dateTo,
    ],
    queryFn: () =>
      listAllSimulations({
        page,
        page_size: pageSize,
        simulator_id:
          simulatorFilter !== "all"
            ? parseInt(simulatorFilter)
            : undefined,
        user_id:
          userFilter !== "all" ? parseInt(userFilter) : undefined,
        date_from: dateFrom || undefined,
        date_to: dateTo || undefined,
        pricing_policy_status:
          policyFilter !== "all" ? policyFilter : undefined,
        shared_only: sharedOnlyParam,
      }),
  });

  const resetFilters = () => {
    setSimulatorFilter("all");
    setUserFilter("all");
    setPolicyFilter("all");
    setSharedFilter("all");
    setDateFrom("");
    setDateTo("");
    setPage(1);
  };

  const hasActiveFilters =
    simulatorFilter !== "all" ||
    userFilter !== "all" ||
    policyFilter !== "all" ||
    sharedFilter !== "all" ||
    dateFrom !== "" ||
    dateTo !== "";

  const handleExportExcel = async () => {
    setIsExporting(true);
    try {
      await exportSimulationsExcel({
        simulator_id:
          simulatorFilter !== "all" ? parseInt(simulatorFilter) : undefined,
        user_id: userFilter !== "all" ? parseInt(userFilter) : undefined,
        date_from: dateFrom || undefined,
        date_to: dateTo || undefined,
        pricing_policy_status:
          policyFilter !== "all" ? policyFilter : undefined,
        shared_only: sharedOnlyParam,
      });
    } catch (error) {
      console.error("Export failed:", error);
    } finally {
      setIsExporting(false);
    }
  };

  const handleOpenInNewTab = (simulation: SimulationAdmin) => {
    if (!simulation.simulator_id) return;

    // Store inputs in sessionStorage so the simulator page can load them
    sessionStorage.setItem(
      `simulation_inputs_${simulation.id}`,
      JSON.stringify(simulation.inputs || {})
    );

    const url = `/simulators/${simulation.simulator_id}/simulate?version=${simulation.version_id}&loadSimulation=${simulation.id}&t=${Date.now()}`;
    window.open(url, "_blank");
  };

  // Statut figé à l'enregistrement de la cotation : une révision de la
  // politique tarifaire ne requalifie pas les cotations déjà établies.
  const getMarginBadge = (simulation: SimulationAdmin) => {
    const politique = simulation.politique_tarifaire;
    if (!politique) {
      return <span className="text-xs text-muted-foreground">—</span>;
    }

    switch (politique.statut) {
      case "conforme":
        return (
          <Badge
            variant="success"
            className="gap-1 whitespace-nowrap"
          >
            <CheckCircle2 className="h-3 w-3" />
            Conforme
          </Badge>
        );
      case "hors_politique":
        return (
          <InfoTooltip
            side="top"
            content={
              <div className="text-xs space-y-1">
                <p className="font-medium">
                  {politique.offre}
                </p>
                <p>
                  Marge attendue :{" "}
                  {politique.marge_min !== null
                    ? politique.marge_min
                    : "—"}{" "}
                  –{" "}
                  {politique.marge_max !== null
                    ? politique.marge_max
                    : "—"}
                </p>
                {politique.derogation && (
                  <p className="text-muted-foreground">
                    Dérogation : {politique.derogation}
                  </p>
                )}
              </div>
            }
          >
            <Badge
              variant="destructive"
              className="gap-1 whitespace-nowrap cursor-help"
            >
              <AlertTriangle className="h-3 w-3" />
              Hors politique
            </Badge>
          </InfoTooltip>
        );
      case "non_defini":
        return (
          <Badge
            variant="secondary"
            className="gap-1 whitespace-nowrap"
          >
            <HelpCircle className="h-3 w-3" />
            Non applicable
          </Badge>
        );
      case "no_policy":
        return (
          <span className="text-xs text-muted-foreground">—</span>
        );
    }
  };

  return (
    <ViewerGuard>
      <div className="flex flex-col h-full">
        <Header title="Toutes les cotations" />

        <div className="flex-1 p-6 space-y-6 overflow-auto">
          {/* Filters */}
          <Card>
            <CardContent className="p-4">
              <div className="flex flex-wrap items-center gap-4">
                <div className="flex items-center gap-2 text-muted-foreground">
                  <Filter className="h-4 w-4" />
                  <span className="text-sm font-medium">
                    Filtres
                  </span>
                </div>

                {/* Simulator filter */}
                <Select
                  value={simulatorFilter}
                  onValueChange={(v) => {
                    setSimulatorFilter(v);
                    setPage(1);
                  }}
                >
                  <SelectTrigger className="w-[200px]">
                    <SelectValue placeholder="Simulateur" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">
                      Tous les simulateurs
                    </SelectItem>
                    {simulatorsData?.items.map((sim) => (
                      <SelectItem
                        key={sim.id}
                        value={String(sim.id)}
                      >
                        {sim.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>

                {/* User filter */}
                <UserFilter
                  value={userFilter}
                  onChange={(v) => {
                    setUserFilter(v);
                    setPage(1);
                  }}
                />

                {/* Pricing policy filter */}
                <Select
                  value={policyFilter}
                  onValueChange={(v) => {
                    setPolicyFilter(v);
                    setPage(1);
                  }}
                >
                  <SelectTrigger className="w-[200px]">
                    <SelectValue placeholder="Taux de marge" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">
                      Taux de marge
                    </SelectItem>
                    <SelectItem value="hors_politique">
                      Non conforme
                    </SelectItem>
                    <SelectItem value="conforme">
                      Conforme
                    </SelectItem>
                  </SelectContent>
                </Select>

                {/* Shared filter */}
                <Select
                  value={sharedFilter}
                  onValueChange={(v) => {
                    setSharedFilter(v as SharedFilterValue);
                    setPage(1);
                  }}
                >
                  <SelectTrigger className="w-[220px]">
                    <SelectValue placeholder="Partage" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">
                      Toutes les cotations
                    </SelectItem>
                    <SelectItem value="shared">
                      Cotations partagées uniquement
                    </SelectItem>
                    <SelectItem value="not_shared">
                      Cotations non partagées
                    </SelectItem>
                  </SelectContent>
                </Select>

                {/* Date range filter */}
                <div className="flex items-center gap-2">
                  <CalendarDays className="h-4 w-4 text-muted-foreground flex-shrink-0" />
                  <div className="flex items-center gap-1.5">
                    <Label htmlFor="date-from" className="sr-only">Du</Label>
                    <Input
                      id="date-from"
                      type="date"
                      value={dateFrom}
                      onChange={(e) => { setDateFrom(e.target.value); setPage(1); }}
                      className="h-9 w-[140px] text-sm"
                      placeholder="Du"
                    />
                    <span className="text-xs text-muted-foreground">–</span>
                    <Label htmlFor="date-to" className="sr-only">Au</Label>
                    <Input
                      id="date-to"
                      type="date"
                      value={dateTo}
                      onChange={(e) => { setDateTo(e.target.value); setPage(1); }}
                      className="h-9 w-[140px] text-sm"
                      placeholder="Au"
                    />
                  </div>
                </div>

                {hasActiveFilters && (
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={resetFilters}
                  >
                    Réinitialiser
                  </Button>
                )}

                <Button
                  variant="outline"
                  size="sm"
                  className="ml-auto gap-2"
                  onClick={handleExportExcel}
                  disabled={isExporting}
                >
                  {isExporting ? (
                    <Loader2 className="h-4 w-4 animate-spin" />
                  ) : (
                    <Download className="h-4 w-4" />
                  )}
                  Export Excel
                </Button>
              </div>
            </CardContent>
          </Card>

          {/* Table */}
          <Card>
            <CardContent className="p-0">
              <div className="overflow-x-auto">
                <Table className="min-w-[1280px]">
                  <TableHeader>
                    <TableRow>
                      <TableHead className="whitespace-nowrap">
                        Date / Heure
                      </TableHead>
                      <TableHead className="whitespace-nowrap">
                        Raison sociale
                      </TableHead>
                      <TableHead className="whitespace-nowrap">
                        Nom et circuit
                      </TableHead>
                      <TableHead className="whitespace-nowrap">
                        Type de cotation
                      </TableHead>
                      <TableHead className="whitespace-nowrap">
                        Utilisateur
                      </TableHead>
                      <TableHead className="whitespace-nowrap">
                        Partage
                      </TableHead>
                      <TableHead className="whitespace-nowrap">
                        Tarif édité
                      </TableHead>
                      <TableHead className="whitespace-nowrap">
                        Taux de marge
                      </TableHead>
                      <TableHead className="whitespace-nowrap">
                        Politique tarifaire
                      </TableHead>
                      <TableHead className="w-[50px]"></TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {isLoading ? (
                      [...Array(5)].map((_, i) => (
                        <TableRow key={i}>
                          {[...Array(10)].map((_, j) => (
                            <TableCell key={j}>
                              <Skeleton className="h-4 w-20" />
                            </TableCell>
                          ))}
                        </TableRow>
                      ))
                    ) : data?.items.length === 0 ? (
                      <TableRow>
                        <TableCell
                          colSpan={10}
                          className="h-32 text-center"
                        >
                          <div className="flex flex-col items-center gap-2 text-muted-foreground">
                            <History className="h-10 w-10 opacity-50" />
                            <p>Aucune cotation trouvée</p>
                            {hasActiveFilters && (
                              <Button
                                variant="outline"
                                size="sm"
                                onClick={resetFilters}
                              >
                                Réinitialiser les filtres
                              </Button>
                            )}
                          </div>
                        </TableCell>
                      </TableRow>
                    ) : (
                      data?.items.map((simulation, index) => (
                        <TableRow
                          key={simulation.id}
                          className="border-b transition-colors hover:bg-muted/50"
                          style={{
                            animation: `fadeInUp 0.3s ease-out ${index * 0.03}s both`,
                          }}
                        >
                          {/* Date/Heure (Paris) */}
                          <TableCell className="text-sm whitespace-nowrap">
                            {formatDateParis(simulation.created_at)}
                          </TableCell>

                          {/* Raison sociale */}
                          <TableCell className="text-sm">
                            <span className="truncate max-w-[180px] block">
                              {(simulation.inputs
                                ?.raison_sociale as string) || "—"}
                            </span>
                          </TableCell>

                          {/* Nom de la cotation, sur deux lignes car sa fin distingue souvent les variantes
                              (50 % / 100 %), puis les circuits, qu'une cotation recopiée garde tels quels */}
                          <TableCell className="text-sm">
                            <div className="flex flex-col gap-0.5 max-w-[240px]">
                              <span
                                className="line-clamp-2 break-words"
                                title={simulation.name || undefined}
                              >
                                {simulation.name || "—"}
                              </span>
                              {simulation.saisies_libres &&
                                simulation.saisies_libres.length > 0 && (
                                  <span
                                    className="text-xs text-muted-foreground truncate"
                                    title={simulation.saisies_libres.join(" · ")}
                                  >
                                    {simulation.saisies_libres.join(" · ")}
                                  </span>
                                )}
                            </div>
                          </TableCell>

                          {/* Type de cotation (nom simulateur) */}
                          <TableCell className="text-sm">
                            <span className="truncate max-w-[180px] block">
                              {simulation.simulator_name || "—"}
                            </span>
                          </TableCell>

                          {/* Utilisateur (email) */}
                          <TableCell className="text-sm">
                            <span
                              className="truncate max-w-[200px] block"
                              title={
                                simulation.user_email || undefined
                              }
                            >
                              {simulation.user_email || "—"}
                            </span>
                          </TableCell>

                          {/* Partage (destinataire) */}
                          <TableCell className="text-sm">
                            {simulation.shared_with_user_id ? (
                              <InfoTooltip
                                side="top"
                                content={
                                  <div className="text-xs space-y-0.5">
                                    <p className="font-medium">
                                      Partagée avec
                                    </p>
                                    <p>
                                      {simulation.shared_with_user_full_name ||
                                        simulation.shared_with_user_email ||
                                        "—"}
                                    </p>
                                    {simulation.shared_with_user_email &&
                                      simulation.shared_with_user_full_name && (
                                        <p className="text-muted-foreground">
                                          {simulation.shared_with_user_email}
                                        </p>
                                      )}
                                  </div>
                                }
                              >
                                <Badge
                                  variant="secondary"
                                  className="gap-1 whitespace-nowrap cursor-help max-w-[220px]"
                                >
                                  <Share2 className="h-3 w-3 flex-shrink-0" />
                                  <span className="truncate">
                                    {simulation.shared_with_user_full_name ||
                                      simulation.shared_with_user_email ||
                                      "—"}
                                  </span>
                                </Badge>
                              </InfoTooltip>
                            ) : (
                              <span className="text-xs text-muted-foreground">
                                —
                              </span>
                            )}
                          </TableCell>

                          {/* Tarif édité (popup hover) */}
                          <TableCell>
                            <InfoTooltip
                              side="left"
                              content={
                                <TarifTooltipContent
                                  simulation={simulation}
                                />
                              }
                            >
                              <button className="flex items-center gap-1.5 text-sm text-primary hover:underline cursor-pointer">
                                <Eye className="h-3.5 w-3.5" />
                                <span>Voir</span>
                              </button>
                            </InfoTooltip>
                          </TableCell>

                          {/* Taux de marge */}
                          <TableCell className="text-sm font-mono">
                            {(() => {
                              const m = getMargeFromInputs(simulation.inputs);
                              return m !== undefined && m !== null ? `${m}` : "—";
                            })()}
                          </TableCell>

                          {/* Hors politique tarifaire */}
                          <TableCell>
                            {getMarginBadge(simulation)}
                          </TableCell>

                          {/* Voir la cotation */}
                          <TableCell>
                            <InfoTooltip
                              side="top"
                              content={
                                <span className="text-xs">
                                  Ouvrir la cotation
                                </span>
                              }
                            >
                              <Button
                                variant="ghost"
                                size="icon"
                                className="h-8 w-8"
                                onClick={() =>
                                  handleOpenInNewTab(simulation)
                                }
                                disabled={!simulation.simulator_id}
                              >
                                <ExternalLink className="h-4 w-4" />
                              </Button>
                            </InfoTooltip>
                          </TableCell>
                        </TableRow>
                      ))
                    )}
                  </TableBody>
                </Table>
              </div>
            </CardContent>
          </Card>

          {/* Pagination */}
          {data && data.total_pages > 1 && (
            <div className="flex items-center justify-between">
              <p className="text-sm text-muted-foreground">
                {data.total_items} cotation
                {data.total_items > 1 ? "s" : ""} au total
              </p>
              <div className="flex gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setPage((p) => Math.max(1, p - 1))}
                  disabled={page === 1}
                >
                  Précédent
                </Button>
                <div className="flex items-center gap-1 px-3 py-1 bg-muted rounded text-sm">
                  {page} / {data.total_pages}
                </div>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() =>
                    setPage((p) =>
                      Math.min(data.total_pages, p + 1)
                    )
                  }
                  disabled={page === data.total_pages}
                >
                  Suivant
                </Button>
              </div>
            </div>
          )}
        </div>
      </div>
    </ViewerGuard>
  );
}
